import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useMediaFetch } from '../hooks/useMediaFetch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SearchHeader from '../components/SearchHeader';
import MediaGrid from '../components/MediaGrid';
import { MediaFile } from '../components/MediaGrid';
import useDebounce from '../hooks/useDebounce';

const Container = styled.div`
  h3 {
    font-family: ralewayBold;
    font-size: 16px;
    font-weight: unset !important;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    padding-bottom: 10px;
  }
`;

type PexelsSectionProps = {
  onSelect: (file: MediaFile) => void;
  onDelete: (id: number) => void;
};

const PexelsSection: React.FC<PexelsSectionProps> = ({
  onSelect,
  onDelete
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const { apiService } = useEditorStore();
  const {
    files,
    loading,
    noResults,
    fetchMedia,
    resetState,
    setLoadingValue,
    clearFiles
  } = useMediaFetch('video', 'pexels');

  const loadMore = async () => {
    if (!canLoadMore) return;
    const hasMore = await fetchMedia(page, debouncedSearch);
    setCanLoadMore(hasMore ?? false);
  };

  const { page, setPage, loadMoreRef, setCanLoadMore, canLoadMore } =
    useInfiniteScroll(loadMore);

  useEffect(() => {
    resetState();
    setPage(1);
    setCanLoadMore(true);
  }, [debouncedSearch]);

  const handleSelect = async (file: MediaFile) => {
    if (!apiService) return;

    setCanLoadMore(false);
    clearFiles();
    setLoadingValue(true);

    try {
      const response = await apiService.downloadPexelsVideo(
        `${file.id}`,
        file.name || ''
      );

      if (response?.video) {
        onSelect(response.video);
        setLoadingValue(false);
      }
    } catch (error) {
      console.error('Error downloading Pexels video:', error);
      setLoadingValue(false);
    }
  };

  return (
    <Container>
      <h3>Pexels</h3>
      <SearchHeader
        placeholder="Search videos"
        value={search}
        onChange={setSearch}
      />
      {files && files.length > 0 && search.trim() !== '' && (
        <h3>Search results for "{search}"</h3>
      )}
      {noResults && search && search.trim() !== '' && (
        <h3>No results found for "{search}"</h3>
      )}
      <MediaGrid
        files={files}
        loading={loading}
        type="video"
        onSelect={handleSelect}
        onDelete={onDelete}
        loadMoreRef={loadMoreRef}
        showNoResults={noResults}
        searchQuery={search}
      />
    </Container>
  );
};

export default PexelsSection;
